import { Component, EventEmitter, Input, Output, inject, signal } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { ModulesService } from '@shared/services/modules.service';
import { pageLoadGate } from '@shared/components/custom-message/custom-message.service';

/**
 * Estado de la carga de un módulo.
 *
 *   readonly pageLoad = new PageLoad();
 *   ngOnInit() { void this.init(); }
 *   init() { return this.pageLoad.run(async () => { ...carga inicial... }); }
 *
 * - `run()`   carga INICIAL: bloquea el módulo hasta que termina. Si algo
 *             lanza, se muestra el error con "Reintentar" (nunca se quita sola).
 * - `refresh()` recargas posteriores (Actualizar, filtros): solo la barrita
 *             de progreso, sin bloquear.
 */
export class PageLoad {
  readonly status = signal<'loading' | 'ready' | 'error'>('loading');
  /** Pasaron 15 s y sigue cargando: se ofrece Reintentar. */
  readonly slow = signal(false);
  readonly error = signal('');
  readonly refreshing = signal(false);
  private timer: any = null;
  private static readonly SLOW_MS = 15000;

  get ready(): boolean { return this.status() === 'ready'; }

  async run(task: () => Promise<any>): Promise<boolean> {
    this.status.set('loading');
    this.slow.set(false);
    this.error.set('');
    clearTimeout(this.timer);
    this.timer = setTimeout(() => this.slow.set(true), PageLoad.SLOW_MS);
    pageLoadGate.active++;
    try {
      await task();
      this.status.set('ready');
      return true;
    } catch (err: any) {
      this.error.set(PageLoad.message(err));
      this.status.set('error');
      return false;
    } finally {
      pageLoadGate.active = Math.max(0, pageLoadGate.active - 1);
      clearTimeout(this.timer);
      this.slow.set(false);
    }
  }

  /** Acepta la función o directamente la promesa: (click)="pageLoad.refresh(search())". */
  async refresh<T>(task: (() => Promise<T>) | Promise<T> | T): Promise<T | undefined> {
    this.refreshing.set(true);
    try { return await (typeof task === 'function' ? (task as () => Promise<T>)() : task); }
    finally { this.refreshing.set(false); }
  }

  private static message(err: any): string {
    if (!err) return 'Ocurrió un error al cargar el módulo.';
    if (typeof err === 'string') return err;
    return err?.error?.message || err?.message || String(err);
  }
}

/**
 * Pantalla de carga del módulo: cubre el área del módulo (debajo del título)
 * con un spinner, el icono y el nombre del módulo, hasta que termina la carga
 * inicial. Durante las recargas muestra solo una barrita arriba.
 * Va justo después de <app-page-title>.
 */
@Component({
  selector: 'app-page-loader',
  standalone: true,
  imports: [MatIconModule, MatButtonModule, MatProgressSpinnerModule, MatProgressBarModule],
  template: `
    @if (load.status() !== 'ready') {
      <div class="page-loader" role="status" aria-live="polite">
        <div class="page-loader__box">
          @if (load.status() === 'loading') {
            <div class="page-loader__spinner">
              <mat-spinner diameter="64" strokeWidth="4"></mat-spinner>
              @if (icon()) {<mat-icon class="page-loader__icon">{{ icon() }}</mat-icon>}
            </div>
            <p class="page-loader__title">Cargando {{ label || 'módulo' }}…</p>
            @if (load.slow()) {
              <p class="page-loader__hint">Está tardando más de lo normal. Revisa tu conexión o vuelve a intentarlo.</p>
              <button mat-stroked-button color="primary" type="button" (click)="retry.emit()"><mat-icon>refresh</mat-icon> Reintentar</button>
            } @else {
              <p class="page-loader__hint">Preparando catálogos y permisos</p>
            }
          } @else {
            <mat-icon class="page-loader__error">cloud_off</mat-icon>
            <p class="page-loader__title">No se pudo cargar {{ label || 'el módulo' }}</p>
            <p class="page-loader__hint">{{ load.error() }}</p>
            <button mat-flat-button color="primary" type="button" (click)="retry.emit()"><mat-icon>refresh</mat-icon> Reintentar</button>
          }
        </div>
      </div>
    } @else if (load.refreshing()) {
      <mat-progress-bar class="page-loader__bar" mode="indeterminate"></mat-progress-bar>
    }
  `,
  styles: [`
    :host { display: contents; }
    .page-loader {
      position: absolute; left: 0; right: 0; top: 68px; z-index: 20;
      height: calc(100vh - 68px);
      display: flex; align-items: center; justify-content: center;
      background: rgba(245, 246, 246, 0.96);
      animation: page-loader-in .15s ease-out;
    }
    @keyframes page-loader-in { from { opacity: 0; } to { opacity: 1; } }
    .page-loader__box { display: flex; flex-direction: column; align-items: center; gap: 10px; max-width: 420px; padding: 24px; text-align: center; }
    .page-loader__spinner { position: relative; width: 64px; height: 64px; }
    .page-loader__icon { position: absolute; inset: 0; margin: auto; color: #1010eb; font-size: 28px; width: 28px; height: 28px; }
    .page-loader__title { margin: 8px 0 0; font-size: 17px; font-weight: 600; color: #170f5f; }
    .page-loader__hint { margin: 0; font-size: 13px; color: #6b6890; }
    .page-loader__error { font-size: 48px; width: 48px; height: 48px; color: #b71c1c; }
    .page-loader__bar { position: sticky; top: 0; z-index: 5; margin: -4px 0 0; height: 3px; }
    button { margin-top: 8px; border-radius: 18px; }
  `],
})
export class PageLoaderComponent {
  @Input({ required: true }) load!: PageLoad;
  /** Nombre del módulo para el texto ("Cargando Folio…"). */
  @Input() label = '';
  @Output() retry = new EventEmitter<void>();

  private readonly route = inject(ActivatedRoute);
  private readonly path = '/' + this.route.snapshot.pathFromRoot
    .map(r => r.url.map(s => s.path).join('/')).filter(Boolean).join('/');
  readonly icon = inject(ModulesService).moduleIcon(this.path);
}
